import React from 'react';

/**
 * Six compact "satellite" service cards that orbit around the 3D globe.
 *
 *   <orbit-wrapper>            pointer-events: none, hidden < md
 *     <orbit-tilt>             static perspective + rotateX(15deg)
 *       <orbit-container>      animated 0deg → 360deg via heroOrbitSpin
 *         <orbit-slot>         static rotate(angle) translate(R) rotate(-angle)
 *           <orbit-card>       animated counter-rotation 0deg → -360deg
 *             card content
 *
 * The static positioning (slot) and the counter-rotation (card) each set
 * `transform`, so they live on separate elements to avoid clobbering each
 * other. The wrapper's `pointer-events: none` lets the globe stay draggable;
 * each card individually re-enables pointer events. Hovering any card pauses
 * both the orbit spin and the per-card counter-rotation (rule in index.css).
 */

const ORBIT_DURATION = 60; // seconds per revolution — slow + elegant.
const ORBIT_RADIUS = 320;  // px from orbit centre; larger than the globe.

const OrbitSatellites = ({ services, activeIndex, onSelect }) => {
    return (
        <div
            className={`hero-orbit-wrapper hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none${
                activeIndex !== null && activeIndex !== undefined ? ' panel-open' : ''
            }`}
            style={{ width: 0, height: 0, zIndex: 10 }}
            data-testid="hero-orbit-wrapper"
        >
            <div
                className="hero-orbit-tilt"
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0,
                    transform: 'perspective(800px) rotateX(15deg)',
                    transformStyle: 'preserve-3d',
                }}
            >
                <div
                    className="hero-orbit-container"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 0,
                        height: 0,
                        animation: `heroOrbitSpin ${ORBIT_DURATION}s linear infinite`,
                        willChange: 'transform',
                        transformStyle: 'preserve-3d',
                    }}
                >
                    {services.map((service, i) => {
                        const angle = (360 / services.length) * i;

                        return (
                            <div
                                key={service.title}
                                className="hero-orbit-slot"
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    transform: `rotate(${angle}deg) translate(${ORBIT_RADIUS}px) rotate(-${angle}deg)`,
                                    transformOrigin: '0 0',
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => onSelect && onSelect(i)}
                                    aria-label={`Open details for ${service.title}`}
                                    aria-expanded={activeIndex === i}
                                    data-testid={`hero-orbit-card-${i + 1}`}
                                    className={`hero-orbit-card${activeIndex === i ? ' is-active' : ''}`}
                                    style={{
                                        // Two CSS animations on one element:
                                        //   1. heroOrbitFadeIn — one-shot opacity 0→1, with per-card delay
                                        //   2. heroOrbitCounter — perpetual counter-rotation
                                        animation:
                                            `heroOrbitFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${1.5 + i * 0.15}s both,` +
                                            `heroOrbitCounter ${ORBIT_DURATION}s linear infinite`,
                                        willChange: 'transform, opacity',
                                    }}
                                >
                                    <span className="hero-orbit-card-icon">
                                        <service.icon className="w-[22px] h-[22px]" strokeWidth={1.8} />
                                    </span>
                                    <span className="hero-orbit-card-title">{service.title}</span>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OrbitSatellites;
